import React, { Suspense } from 'react';
import { ErrorBoundary, useSignal } from '@funtech-int/rival-react';

import { InfiniteScrollSentinel } from '@/shared/components/InfiniteScrollSentinel/InfiniteScrollSentinel';
import { PageSpinner } from '@/shared/components/PageSpinner';
import { RemountOnKey } from '@/shared/components/RemountOnKey/RemountOnKey';
import { useProfileManager } from '@/shared/hooks/useProfileManager';

import { getChannelCategoryIdsByTabId } from '../../lib/utils/getChannelCategoryIdsByTabId';
import { useChannelCategoryTabQueryParam } from '../../hooks/useChannelCategoryTabQueryParam';
import { useChannelListQuery } from '../../hooks/useChannelListQuery';
import { ChannelCardContainer } from '../ChannelCard/ChannelCard';

import styles from './ChannelList.module.css';

interface Props {
    categoryIds: number[];
}
const ChannelList = ({ categoryIds }: Props) => {
    const { channelList, pagination } = useChannelListQuery({
        categoryIds,
    });

    const channels = channelList?.channels;

    return (
        <div className={styles.wrapper}>
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ul className={styles.list} role="list">
                {channels?.map((channel) => (
                    // eslint-disable-next-line jsx-a11y/no-redundant-roles
                    <li key={channel.contentId} className={styles.card} role="listitem">
                        <ChannelCardContainer channel={channel} key={channel.contentId} />
                    </li>
                ))}
            </ul>
            <InfiniteScrollSentinel pagination={pagination} />
        </div>
    );
};

export const ChannelListContainer = () => {
    const currentCategoryTabId = useChannelCategoryTabQueryParam();
    const categoryIds = getChannelCategoryIdsByTabId(currentCategoryTabId);
    const billingFeaturesCount = useSignal(useProfileManager().billingFeatureNames).length;

    return (
        <RemountOnKey mountKey={billingFeaturesCount}>
            <ErrorBoundary fallback={() => null}>
                <Suspense fallback={<PageSpinner loadMore center />}>
                    <ChannelList
                        key={categoryIds.join(',') + billingFeaturesCount.toString()}
                        categoryIds={categoryIds}
                    />
                </Suspense>
            </ErrorBoundary>
        </RemountOnKey>
    );
};
